import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import classNames from 'classnames';
import {
  AiDocumentationFieldEnum,
  DocumentationValidationStatusEnum,
} from 'graphql-common';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import Chip from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import DocumentationValidationStatusChip from '@lib/components/DocumentationValidationStatusChip/DocumentationValidationStatusChip';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import formatReadingDurationHms from '@lib/utils/formatReadingDurationHms';
import UserChip from '@lib/components/UserChip/UserChip';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import useEdmDocumentationQueryHook from 'utils/fetch/hooks/useEdmDocumentationQueryHook';
import { DocumentFieldDetection } from '../utils/useDocumentFieldDetection';
import styles from './DocumentInfo.module.scss';

type Props = {
  documentQueryHookResult: ReturnType<typeof useEdmDocumentationQueryHook>;
  isViewLocked?: boolean;
  loading: boolean;
  onValidationStatusChange: (
    id: string,
    newStatus: DocumentationValidationStatusEnum,
  ) => void;
  validationStatusLoading: boolean;
  // AI code/category detection state (undefined when AI detection is off).
  codeDetection?: DocumentFieldDetection['code'];
  categoryDetection?: DocumentFieldDetection['category'];
  credits?: AiCredits | null;
};

export default function DocumentInfo({
  documentQueryHookResult,
  isViewLocked,
  loading,
  onValidationStatusChange,
  validationStatusLoading,
  codeDetection,
  categoryDetection,
  credits,
}: Props) {
  const { t } = useTranslation();
  const { data } = documentQueryHookResult;
  const {
    code,
    category,
    createdAt,
    updatedAt,
    createdBy,
    renewalDeadlineDate,
    approver,
    totalEngagementSeconds,
  } = data || {};

  const showCodeAssist =
    codeDetection && codeDetection.uiState !== AiFieldUiState.None;
  // CODE's MultipleCodes/Failed states can still carry a value (a
  // low-confidence detected code alongside several suggestedCodes, or a
  // previously-typed value that failed re-detection) — show it next to the
  // assist trigger, matching the old AiCodeDetectionValue's behavior. The
  // Detecting placeholder is the entire content on its own, same as before.
  const showCodeValueBesideAssist =
    showCodeAssist &&
    code &&
    (codeDetection?.uiState === AiFieldUiState.MultipleCodes ||
      codeDetection?.uiState === AiFieldUiState.Failed);

  const showCategoryAssist =
    categoryDetection && categoryDetection.uiState !== AiFieldUiState.None;

  let categoryContent: React.ReactNode;
  if (showCategoryAssist && categoryDetection) {
    categoryContent = (
      <AiFieldAssist
        field={AiDocumentationFieldEnum.Category}
        resolveSource={categoryDetection.resolveSource}
        onApplyCategoryId={categoryDetection.onApplyCategoryId}
        detectionUiState={categoryDetection.uiState}
        // Mirrors getCategoryFields.tsx's existing (pre-existing, out of
        // scope) reuse of the CODE detecting copy for CATEGORY — kept
        // identical here for consistency with the form rather than
        // introducing a new label.
        detectingLabel={t('ai-detecting-code')}
        onDismiss={categoryDetection.onDismiss}
        creditsRemaining={credits}
        labels={{
          idleLabel: t('ai-suggest-with-ai'),
          idleCta: t('ai-suggest-category-cta'),
          regenerateCta: t('ai-regenerate'),
        }}
      />
    );
  } else if (category) {
    categoryContent = (
      <Chip
        type={colorToChipType(_get(category, 'color'))}
        label={_get(category, 'name')}
        leftIcon="circle"
      />
    );
  } else {
    categoryContent = <Typography variant="body">-</Typography>;
  }

  return (
    <CircleLoaderWrapper
      isLoading={loading}
      type={CircleLoaderWrapperType.blur}
    >
      <div>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="password" symbolsOutlined />
            <Typography variant="caption">{t('code')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            {showCodeAssist && codeDetection ? (
              <span className={styles.valueWithAssist}>
                {showCodeValueBesideAssist && (
                  <Typography variant="body">{code}</Typography>
                )}
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Code}
                  resolveSource={codeDetection.resolveSource}
                  onApplyValue={codeDetection.onApplyValue}
                  detectionUiState={codeDetection.uiState}
                  suggestedCodes={codeDetection.suggestedCodes}
                  detectingLabel={t('ai-detecting-code')}
                  onDismiss={codeDetection.onDismiss}
                  showManualCodeInput
                  isSavingManualCode={codeDetection.isSaving}
                  creditsRemaining={credits}
                  labels={{
                    idleLabel: t('ai-detect-with-ai'),
                    idleCta: t('ai-detect-code-cta'),
                    regenerateCta: t('ai-detect-code-again'),
                  }}
                />
              </span>
            ) : (
              <Typography variant="body">{code || '-'}</Typography>
            )}
          </div>
        </div>
        {!isViewLocked && (
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="category" symbolsOutlined />
              <Typography variant="caption">{t('category')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              {categoryContent}
            </div>
          </div>
        )}
        {!isViewLocked && (
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="verified_user" symbolsOutlined />
              <Typography variant="caption">{t('validation')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              {data ? (
                <DocumentationValidationStatusChip
                  documentation={data}
                  onValidationStatusChangeHandler={
                    data.canUpdateValidationStatus?.value
                      ? onValidationStatusChange
                      : undefined
                  }
                  loading={validationStatusLoading}
                />
              ) : (
                <Typography variant="body">-</Typography>
              )}
            </div>
          </div>
        )}
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="calendar_today" symbolsOutlined />
            <Typography variant="caption">{t('date-created')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">
              {getFormattedDate(createdAt)}
            </Typography>
          </div>
        </div>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="update" symbolsOutlined />
            <Typography variant="caption">{t('last-updated')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">
              {getFormattedDate(updatedAt)}
            </Typography>
          </div>
        </div>
        {!isViewLocked && (
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="event" symbolsOutlined />
              <Typography variant="caption">{t('planned-update')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              <Typography variant="body">
                {renewalDeadlineDate
                  ? getFormattedDate(renewalDeadlineDate)
                  : '-'}
              </Typography>
            </div>
          </div>
        )}
        {!isViewLocked && (
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="schedule" symbolsOutlined />
              <Typography variant="caption">
                {t('total-engagement-time')}
              </Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              <Typography variant="body">
                {totalEngagementSeconds != null
                  ? formatReadingDurationHms(totalEngagementSeconds)
                  : '-'}
              </Typography>
            </div>
          </div>
        )}
        <div
          className={classNames(
            entityViewStyles.infoItem,
            entityViewStyles.infoItemCentered,
          )}
        >
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="person_edit" symbolsOutlined />
            <Typography variant="caption">{t('created-by')}</Typography>
          </p>
          <div
            className={classNames(
              entityViewStyles.infoItemContent,
              entityViewStyles.infoItemContentFullWidth,
            )}
          >
            {createdBy ? (
              <UserChip user={createdBy} />
            ) : (
              <Typography variant="body">-</Typography>
            )}
          </div>
        </div>
        {!isViewLocked && (
          <div
            className={classNames(
              entityViewStyles.infoItem,
              entityViewStyles.infoItemCentered,
            )}
          >
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="how_to_reg" symbolsOutlined />
              <Typography variant="caption">{t('approver')}</Typography>
            </p>
            <div
              className={classNames(
                entityViewStyles.infoItemContent,
                entityViewStyles.infoItemContentFullWidth,
              )}
            >
              {approver ? (
                <UserChip user={approver} />
              ) : (
                <Typography variant="body">-</Typography>
              )}
            </div>
          </div>
        )}
      </div>
    </CircleLoaderWrapper>
  );
}
